import { useCallback, useMemo, useState } from 'react'
import { sileo } from 'sileo'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  useMutationCreateBankAccountInitialBalance,
  useMutationUpdateBankAccountInitialBalance,
  useMutationDeleteBankAccountInitialBalance,
  useQueryBankAccountBanks,
  useQueryBankAccountInitialBalances
} from '../../../../../../../queries/bankAccountQueries'
import {
  parseDisplayNumber,
  formatBaseDisplayValue
} from '../utils/visionDelNegocio.utils'

dayjs.extend(utc)

const EMPTY_INITIAL_BALANCE_FORM = {
  bankId: '',
  initialBalance: ''
}

const createEmptyInitialBalanceForm = () => ({ ...EMPTY_INITIAL_BALANCE_FORM })

export const useInitialBalancesController = ({ businessId, currentBusiness }) => {
  const banksQuery = useQueryBankAccountBanks(businessId, { enabled: !!businessId })
  const initialBalancesQuery = useQueryBankAccountInitialBalances(businessId, undefined, {
    enabled: !!businessId
  })
  const createInitialBalanceMutation = useMutationCreateBankAccountInitialBalance()
  const updateInitialBalanceMutation = useMutationUpdateBankAccountInitialBalance()
  const deleteInitialBalanceMutation = useMutationDeleteBankAccountInitialBalance()

  const [openInitialBalanceModal, setOpenInitialBalanceModal] = useState(false)
  const [deletingInitialBalanceId, setDeletingInitialBalanceId] = useState(null)
  const [editingInitialBalanceId, setEditingInitialBalanceId] = useState(null)
  const [initialBalanceForm, setInitialBalanceForm] = useState(createEmptyInitialBalanceForm)
  const [initialBalanceDrafts, setInitialBalanceDrafts] = useState([createEmptyInitialBalanceForm()])

  const bankCatalog = banksQuery.data || []
  const initialBalances = initialBalancesQuery.data || []
  const initialBalanceRows = useMemo(() => {
    if (!Array.isArray(initialBalances) || initialBalances.length === 0) return []

    return [...initialBalances]
      .sort((a, b) => {
        const bankNameA = String(a?.bankName ?? a?.bank_name ?? '').toLowerCase()
        const bankNameB = String(b?.bankName ?? b?.bank_name ?? '').toLowerCase()
        if (bankNameA !== bankNameB) return bankNameA.localeCompare(bankNameB)

        const effectiveDateA = a?.effectiveDate ?? a?.effective_date ?? null
        const effectiveDateB = b?.effectiveDate ?? b?.effective_date ?? null
        const sortDateA = effectiveDateA ? dayjs.utc(effectiveDateA) : null
        const sortDateB = effectiveDateB ? dayjs.utc(effectiveDateB) : null
        const valueA = sortDateA && sortDateA.isValid() ? sortDateA.valueOf() : 0
        const valueB = sortDateB && sortDateB.isValid() ? sortDateB.valueOf() : 0
        return valueB - valueA
      })
      .map(item => {
        const effectiveDate = item?.effectiveDate ?? item?.effective_date ?? null
        const parsedEffectiveDate = effectiveDate ? dayjs.utc(effectiveDate) : null
        const hasValidEffectiveDate = parsedEffectiveDate && parsedEffectiveDate.isValid()
        const initialBalanceValue = parseDisplayNumber(item?.initialBalance ?? item?.initial_balance ?? 0)

        return {
          key: String(item?.id ?? `${item?.bankId ?? item?.bank_id ?? 'bank'}-${effectiveDate ?? ''}`),
          id: Number(item?.id ?? 0) || null,
          bankId: Number(item?.bankId ?? item?.bank_id ?? 0) || null,
          bankName: item?.bankName ?? item?.bank_name ?? `Banco #${item?.bankId ?? item?.bank_id ?? '-'}`,
          initialBalanceValue,
          balanceLabel: formatBaseDisplayValue(initialBalanceValue),
          effectiveDate,
          effectiveDateLabel: hasValidEffectiveDate ? parsedEffectiveDate.format('DD/MM/YYYY') : 'Sin fecha'
        }
      })
  }, [initialBalances])
  const hasInitialBalances = initialBalanceRows.length > 0
  const initialBalanceTotal = useMemo(() => {
    if (!Array.isArray(initialBalances) || initialBalances.length === 0) return 0

    return initialBalances.reduce((acc, item) => {
      return acc + parseDisplayNumber(item?.initialBalance ?? item?.initial_balance ?? 0)
    }, 0)
  }, [initialBalances])
  const businessCreatedYear = useMemo(() => {
    const createdAt = currentBusiness?.createdAt ?? currentBusiness?.created_at
    const createdDate = createdAt ? new Date(createdAt) : null
    if (createdDate && !Number.isNaN(createdDate.getTime())) {
      return createdDate.getFullYear()
    }
    return new Date().getFullYear()
  }, [currentBusiness?.createdAt, currentBusiness?.created_at])
  const isEditingInitialBalance = editingInitialBalanceId != null
  const isSavingInitialBalance =
    createInitialBalanceMutation.isPending || updateInitialBalanceMutation.isPending

  const resetInitialBalanceForm = useCallback(() => {
    setEditingInitialBalanceId(null)
    setInitialBalanceForm(createEmptyInitialBalanceForm())
    setInitialBalanceDrafts([createEmptyInitialBalanceForm()])
  }, [])

  const closeInitialBalanceModal = useCallback(() => {
    setOpenInitialBalanceModal(false)
    resetInitialBalanceForm()
  }, [resetInitialBalanceForm])

  const formatInitialBalanceCurrencyInput = useCallback(value => {
    const raw = typeof value === 'string' ? value : String(value ?? '')
    const trimmed = raw.trim()

    if (!trimmed) return ''

    const isNegative = trimmed.startsWith('-')
    const digits = trimmed.replace(/[^\d]/g, '')

    if (!digits) return ''

    const formattedDigits = Number(digits).toLocaleString('es-CL')
    return `${isNegative ? '-$ ' : '$ '}${formattedDigits}`
  }, [])

  const handleOpenInitialBalanceModal = useCallback(() => {
    setOpenInitialBalanceModal(true)
  }, [])

  const getInitialBalanceErrorMessage = useCallback(error => {
    const status = error?.response?.status
    const message = error?.response?.data?.message
    const correlationId = error?.response?.data?.correlationId
    const suffix = correlationId ? ` (cid: ${correlationId})` : ''

    if (status === 409) {
      return `Ya existe un saldo inicial para ese banco en ese año.${suffix}`
    }
    if (status === 401) {
      return `Tu sesión no es válida o expiró. Vuelve a iniciar sesión.${suffix}`
    }
    if (status === 404) {
      if (typeof message === 'string' && message.trim()) return `${message}${suffix}`
      return `No se encontró el banco o la empresa solicitada.${suffix}`
    }
    if (Array.isArray(message)) return `${message.join(' · ')}${suffix}`
    if (typeof message === 'string' && message.trim()) return `${message}${suffix}`
    return `${error?.message || 'No se pudo guardar el saldo inicial.'}${suffix}`
  }, [])

  const getDeleteInitialBalanceErrorMessage = useCallback(error => {
    const status = error?.response?.status
    const message = error?.response?.data?.message
    const correlationId = error?.response?.data?.correlationId
    const suffix = correlationId ? ` (cid: ${correlationId})` : ''

    if (status === 404) {
      if (typeof message === 'string' && message.trim()) return `${message}${suffix}`
      return `No se encontró el saldo inicial solicitado.${suffix}`
    }
    if (status === 401) {
      return `Tu sesión no es válida o expiró. Vuelve a iniciar sesión.${suffix}`
    }
    if (Array.isArray(message)) return `${message.join(' · ')}${suffix}`
    if (typeof message === 'string' && message.trim()) return `${message}${suffix}`
    return `${error?.message || 'No se pudo eliminar el saldo inicial.'}${suffix}`
  }, [])

  const getUpdateInitialBalanceErrorMessage = useCallback(error => {
    const status = error?.response?.status
    const message = error?.response?.data?.message
    const correlationId = error?.response?.data?.correlationId
    const suffix = correlationId ? ` (cid: ${correlationId})` : ''

    if (status === 404) {
      if (typeof message === 'string' && message.trim()) return `${message}${suffix}`
      return `No se encontró el saldo inicial solicitado.${suffix}`
    }
    if (status === 401) {
      return `Tu sesión no es válida o expiró. Vuelve a iniciar sesión.${suffix}`
    }
    if (Array.isArray(message)) return `${message.join(' · ')}${suffix}`
    if (typeof message === 'string' && message.trim()) return `${message}${suffix}`
    return `${error?.message || 'No se pudo actualizar el saldo inicial.'}${suffix}`
  }, [])

  const handleChangeInitialBalanceForm = useCallback((field, value) => {
    setInitialBalanceForm(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleEditInitialBalance = useCallback(row => {
    const initialBalanceId = Number(row?.id ?? 0)
    if (!initialBalanceId) return

    setEditingInitialBalanceId(initialBalanceId)
    setInitialBalanceForm({
      bankId: String(row?.bankId ?? ''),
      initialBalance: formatInitialBalanceCurrencyInput(row?.initialBalanceValue ?? 0)
    })
  }, [formatInitialBalanceCurrencyInput])

  const handleAddInitialBalanceDraft = useCallback(() => {
    setInitialBalanceDrafts(prev => [...prev, createEmptyInitialBalanceForm()])
  }, [])

  const handleRemoveInitialBalanceDraft = useCallback(indexToRemove => {
    setInitialBalanceDrafts(prev => {
      if (prev.length <= 1) return prev
      return prev.filter((_, index) => index !== indexToRemove)
    })
  }, [])

  const handleChangeInitialBalanceDraft = useCallback((indexToUpdate, field, value) => {
    setInitialBalanceDrafts(prev =>
      prev.map((item, index) => (
        index === indexToUpdate
          ? { ...item, [field]: value }
          : item
      ))
    )
  }, [])

  const handleCancelInitialBalanceEdit = useCallback(() => {
    resetInitialBalanceForm()
  }, [resetInitialBalanceForm])

  const handleDeleteInitialBalance = useCallback(async row => {
    const initialBalanceId = Number(row?.id ?? 0)
    if (!initialBalanceId) return

    const shouldDelete = window.confirm('¿Quieres eliminar este saldo inicial?')
    if (!shouldDelete) return

    try {
      setDeletingInitialBalanceId(initialBalanceId)
      await deleteInitialBalanceMutation.mutateAsync({
        businessId,
        initialBalanceId
      })

      await initialBalancesQuery.refetch()
      if (editingInitialBalanceId === initialBalanceId) {
        resetInitialBalanceForm()
      }

      sileo.success({
        title: 'Saldo inicial eliminado',
        description: 'El saldo inicial se eliminó correctamente.'
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo eliminar el saldo inicial',
        description: getDeleteInitialBalanceErrorMessage(error)
      })
    } finally {
      setDeletingInitialBalanceId(null)
    }
  }, [
    businessId,
    deleteInitialBalanceMutation,
    editingInitialBalanceId,
    getDeleteInitialBalanceErrorMessage,
    initialBalancesQuery,
    resetInitialBalanceForm
  ])

  const handleSubmitInitialBalance = useCallback(async event => {
    event.preventDefault()

    if (isEditingInitialBalance) {
      if (!initialBalanceForm.bankId) {
        sileo.warning({
          title: 'Datos incompletos',
          description: 'Debes seleccionar banco.'
        })
        return
      }

      if (initialBalanceForm.initialBalance === '') {
        sileo.warning({
          title: 'Saldo requerido',
          description: 'Debes ingresar un saldo inicial.'
        })
        return
      }

      const initialBalance = parseDisplayNumber(initialBalanceForm.initialBalance)
      if (!Number.isFinite(initialBalance)) {
        sileo.warning({
          title: 'Saldo inválido',
          description: 'Ingresa un monto válido para saldo inicial.'
        })
        return
      }

      if (initialBalance < 0) {
        sileo.warning({
          title: 'Saldo inválido',
          description: 'El saldo inicial no puede ser negativo.'
        })
        return
      }

      const initialBalanceId = Number(editingInitialBalanceId ?? 0)
      if (!initialBalanceId) return

      try {
        await updateInitialBalanceMutation.mutateAsync({
          businessId,
          initialBalanceId,
          payload: { initialBalance }
        })

        await initialBalancesQuery.refetch()
        resetInitialBalanceForm()

        sileo.success({
          title: 'Saldo inicial actualizado',
          description: 'El saldo inicial se actualizó correctamente.'
        })
      } catch (error) {
        sileo.error({
          title: 'No se pudo actualizar el saldo inicial',
          description: getUpdateInitialBalanceErrorMessage(error)
        })
      }
      return
    }

    const selectedYear = businessCreatedYear
    const effectiveDate = `${selectedYear}-01-01`
    const nonEmptyDrafts = initialBalanceDrafts.filter(item => item.bankId || item.initialBalance !== '')

    if (nonEmptyDrafts.length === 0) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'Debes agregar al menos un saldo inicial.'
      })
      return
    }

    if (nonEmptyDrafts.length !== initialBalanceDrafts.length) {
      sileo.warning({
        title: 'Filas incompletas',
        description: 'Completa o elimina las filas vacías antes de guardar.'
      })
      return
    }

    const payloads = []
    const requestKeys = new Set()

    for (const [index, draft] of nonEmptyDrafts.entries()) {
      const bankId = Number(draft.bankId)
      if (!bankId) {
        sileo.warning({
          title: 'Datos incompletos',
          description: `Debes seleccionar banco en la fila ${index + 1}.`
        })
        return
      }

      if (draft.initialBalance === '') {
        sileo.warning({
          title: 'Saldo requerido',
          description: `Debes ingresar un saldo inicial en la fila ${index + 1}.`
        })
        return
      }

      const initialBalance = parseDisplayNumber(draft.initialBalance)
      if (!Number.isFinite(initialBalance)) {
        sileo.warning({
          title: 'Saldo inválido',
          description: `Ingresa un monto válido en la fila ${index + 1}.`
        })
        return
      }

      if (initialBalance < 0) {
        sileo.warning({
          title: 'Saldo inválido',
          description: `El saldo inicial no puede ser negativo en la fila ${index + 1}.`
        })
        return
      }

      const requestKey = `${bankId}-${effectiveDate}`
      if (requestKeys.has(requestKey)) {
        sileo.warning({
          title: 'Banco duplicado',
          description: 'No puedes repetir el mismo banco dentro del mismo guardado.'
        })
        return
      }

      const hasDuplicateYear = initialBalances.some(item => {
        const itemBankId = Number(item?.bankId ?? item?.bank_id ?? 0)
        const itemYear = new Date(item?.effectiveDate ?? item?.effective_date ?? 0).getFullYear()
        return itemBankId === bankId && itemYear === selectedYear
      })

      if (hasDuplicateYear) {
        sileo.warning({
          title: 'Saldo ya existe',
          description: 'Ya existe un saldo inicial para uno de los bancos seleccionados en ese año.'
        })
        return
      }

      requestKeys.add(requestKey)
      payloads.push({
        bankId,
        initialBalance,
        effectiveDate
      })
    }

    try {
      await createInitialBalanceMutation.mutateAsync({
        businessId,
        payload: payloads.length === 1 ? payloads[0] : payloads
      })

      await initialBalancesQuery.refetch()
      resetInitialBalanceForm()

      if (payloads.length === 1) {
        sileo.success({
          title: 'Saldo inicial guardado',
          description: 'El saldo inicial por banco se creó correctamente.'
        })
      } else {
        sileo.success({
          title: 'Saldos iniciales guardados',
          description: `Se crearon ${payloads.length} saldos iniciales correctamente.`
        })
      }
    } catch (error) {
      sileo.error({
        title: 'No se pudo guardar el saldo inicial',
        description: getInitialBalanceErrorMessage(error)
      })
    }
  }, [
    businessCreatedYear,
    businessId,
    createInitialBalanceMutation,
    editingInitialBalanceId,
    getInitialBalanceErrorMessage,
    getUpdateInitialBalanceErrorMessage,
    initialBalanceDrafts,
    initialBalanceForm.bankId,
    initialBalanceForm.initialBalance,
    initialBalances,
    initialBalancesQuery,
    isEditingInitialBalance,
    resetInitialBalanceForm,
    updateInitialBalanceMutation
  ])

  return {
    openInitialBalanceModal,
    bankCatalog,
    banksLoading: banksQuery.isLoading,
    initialBalancesLoading: initialBalancesQuery.isLoading,
    initialBalanceRows,
    hasInitialBalances,
    initialBalanceTotal,
    businessCreatedYear,
    deletingInitialBalanceId,
    editingInitialBalanceId,
    initialBalanceForm,
    initialBalanceDrafts,
    isEditingInitialBalance,
    isSavingInitialBalance,
    handleOpenInitialBalanceModal,
    closeInitialBalanceModal,
    handleSubmitInitialBalance,
    handleEditInitialBalance,
    handleDeleteInitialBalance,
    handleCancelInitialBalanceEdit,
    handleAddInitialBalanceDraft,
    handleRemoveInitialBalanceDraft,
    handleChangeInitialBalanceDraft,
    handleChangeInitialBalanceForm,
    formatInitialBalanceCurrencyInput
  }
}
